/**
 * Browse Assets page — search, filter, sort, paginate.
 * Route: #/browse
 */

import { router } from '../router.js';
import {
  browseAssets,
  getStatusLabels,
  getCategories,
  getManufacturers,
  getModels,
  getLocations,
} from '../snipeit.js';
import { addRecent, pinFilters } from '../browse-presets.js';
import { showToast } from '../toast.js';
import { escapeHtml, statusClass } from '../asset-detail-helpers.js';
import { MAC_COLUMN_WLAN, MAC_COLUMN_LAN, MAC_COLUMN_IP } from '../settings.js';

const PAGE_SIZE = 50;

// Session-level cache for filter dimension values
const dimCache = new Map();

const DIMENSIONS = [
  { key: 'status_id',       label: 'Status',       loader: getStatusLabels },
  { key: 'category_id',     label: 'Category',     loader: getCategories   },
  { key: 'manufacturer_id', label: 'Manufacturer', loader: getManufacturers },
  { key: 'model_id',        label: 'Model',        loader: getModels       },
  { key: 'location_id',     label: 'Location',     loader: getLocations    },
];

const SORT_FIELDS = [
  { key: 'asset_tag', label: 'Asset Tag' },
  { key: 'model',     label: 'Model' },
  { key: 'category',  label: 'Category' },
  { key: 'status_label', label: 'Status' },
  { key: 'location',  label: 'Location' },
];

export async function renderBrowse() {
  const app = document.getElementById('app');

  // ── State ──
  let filters = {};  // { status_id: '1', category_id: '3', ... }
  let labels  = {};  // { status_id: 'Deployed', category_id: 'Laptops', ... }
  let search  = '';
  let sortField = 'asset_tag';
  let sortOrder = 'asc';
  let offset  = 0;
  let total   = 0;
  let assets  = [];
  let loading = false;
  let infiniteScrollActive = false;
  let observer = null;

  // ── Prefill from Home preset tap ──
  const prefillRaw = sessionStorage.getItem('browse_prefill');
  if (prefillRaw) {
    sessionStorage.removeItem('browse_prefill');
    try {
      const pf = JSON.parse(prefillRaw);
      if (pf.filters) filters = pf.filters;
      if (pf.labels)  labels  = pf.labels;
    } catch { /* ignore */ }
  }

  // ── Render shell ──
  app.innerHTML = `
    <div class="page browse-page">
      <div class="page-header">
        <button class="back-btn" id="browse-back" aria-label="Back">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <h1>Browse Assets</h1>
        <button class="btn btn-primary btn-sm hidden" id="browse-save">Save</button>
      </div>

      <div class="browse-search-wrap">
        <span class="search-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        </span>
        <input type="text" id="browse-search" class="form-control" placeholder="Search assets..." />
      </div>

      <div class="browse-chip-row" id="chip-row"></div>

      <div class="browse-sort-row" id="sort-row"></div>

      <div class="browse-results-header" id="results-header"></div>

      <div id="asset-list"></div>

      <div id="load-more-wrap" class="browse-load-more hidden">
        <button class="btn btn-secondary" id="load-more-btn">Keep Loading</button>
        <span id="load-more-sub" class="browse-load-more-sub"></span>
      </div>

      <div id="browse-sentinel" style="height:1px;"></div>
    </div>
  `;

  // ── Element refs ──
  const backBtn    = document.getElementById('browse-back');
  const saveBtn    = document.getElementById('browse-save');
  const searchInput = document.getElementById('browse-search');
  const chipRow    = document.getElementById('chip-row');
  const sortRow    = document.getElementById('sort-row');
  const resultsHdr = document.getElementById('results-header');
  const assetList  = document.getElementById('asset-list');
  const lmWrap     = document.getElementById('load-more-wrap');
  const lmBtn      = document.getElementById('load-more-btn');
  const lmSub      = document.getElementById('load-more-sub');
  const sentinel   = document.getElementById('browse-sentinel');

  // ── Back ──
  backBtn.addEventListener('click', () => router.navigate('/'));

  // ── Save ──
  saveBtn.addEventListener('click', () => {
    const defaultName = Object.values(labels).filter(Boolean).join(' \u00b7 ') || 'My Search';
    const name = prompt('Name this search shortcut:', defaultName);
    if (name === null) return;
    pinFilters({ ...filters }, { ...labels }, name.trim() || defaultName);
    showToast(`"${name || defaultName}" saved`);
  });

  // ── Search (debounced) ──
  let searchTimer;
  searchInput.addEventListener('input', () => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      search = searchInput.value.trim();
      resetAndFetch();
    }, 300);
  });

  // ── Sort chips ──
  function renderSortRow() {
    sortRow.innerHTML = '<span class="browse-sort-label">Sort:</span>' +
      SORT_FIELDS.map(f => {
        const active = sortField === f.key;
        const arrow = active ? (sortOrder === 'asc' ? ' \u2191' : ' \u2193') : '';
        return `<button class="browse-sort-chip${active ? ' active' : ''}" data-key="${f.key}">${f.label}${arrow}</button>`;
      }).join('');
  }

  sortRow.addEventListener('click', (e) => {
    const chip = e.target.closest('.browse-sort-chip');
    if (!chip) return;
    const key = chip.dataset.key;
    if (sortField === key) {
      sortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      sortField = key;
      sortOrder = 'asc';
    }
    renderSortRow();
    resetAndFetch();
  });

  // ── Filter chips ──
  function renderChips() {
    const chips = Object.entries(filters).map(([dim, id]) => {
      const label = labels[dim] || id;
      return `<span class="filter-chip active" data-dim="${dim}">${escapeHtml(label)} <span class="filter-chip-x">\u2715</span></span>`;
    });
    chips.push('<button class="filter-chip add" id="add-filter-btn">\uff0b Add filter</button>');
    chipRow.innerHTML = chips.join('');

    // Toggle save button visibility
    const hasFilters = Object.keys(filters).length > 0 || search;
    saveBtn.classList.toggle('hidden', !hasFilters);
  }

  chipRow.addEventListener('click', (e) => {
    // Remove filter
    const x = e.target.closest('.filter-chip-x');
    if (x) {
      const chip = x.closest('.filter-chip');
      const dim = chip?.dataset.dim;
      if (dim) {
        delete filters[dim];
        delete labels[dim];
        renderChips();
        resetAndFetch();
      }
      return;
    }
    // Add filter
    if (e.target.closest('#add-filter-btn')) {
      openFilterSheet();
    }
  });

  // ── Fetch ──
  async function fetchAssets(append = false) {
    if (loading) return;
    loading = true;

    if (!append) {
      assetList.innerHTML = '<div style="text-align:center; padding:24px;"><div class="spinner"></div></div>';
      lmWrap.classList.add('hidden');
    } else {
      lmBtn.disabled = true;
      lmBtn.textContent = 'Loading\u2026';
    }

    try {
      const res = await browseAssets({
        search,
        ...filters,
        sort: sortField,
        order: sortOrder,
        offset,
        limit: PAGE_SIZE,
      });
      total = res.total || 0;
      const rows = res.rows || [];

      if (append) {
        assets = assets.concat(rows);
        renderAssetRows(rows, true);
      } else {
        assets = rows;
        renderAssetRows(rows, false);
      }

      resultsHdr.textContent = `${total} asset${total !== 1 ? 's' : ''}`;

      // Show/hide load more
      if (assets.length < total) {
        lmWrap.classList.remove('hidden');
        lmBtn.disabled = false;
        lmBtn.textContent = infiniteScrollActive ? 'Loading\u2026' : 'Keep Loading';
        lmSub.textContent = `Showing ${assets.length} of ${total}`;
      } else {
        lmWrap.classList.add('hidden');
      }

      // Save to recents
      if (Object.keys(filters).length > 0) {
        addRecent({ ...filters }, { ...labels });
      }
    } catch (err) {
      if (!append) {
        assetList.innerHTML = `<div style="text-align:center; padding:24px; color:var(--color-danger);">Failed to load: ${escapeHtml(err.message)}</div>`;
      } else {
        showToast('Failed to load more');
      }
    } finally {
      loading = false;
    }
  }

  function resetAndFetch() {
    offset = 0;
    assets = [];
    infiniteScrollActive = false;
    if (observer) { observer.disconnect(); observer = null; }
    fetchAssets(false);
  }

  // ── Render asset rows ──
  function renderAssetRows(rows, append) {
    const html = rows.map(a => {
      const sName = a.status_label?.name || '\u2014';
      const sClass = statusClass(a.status_label?.status_type || '');
      const model = a.model?.name || '';
      const serial = a.serial || '';
      return `
        <div class="browse-asset-row" data-id="${a.id}">
          <div class="browse-asset-info">
            <div class="browse-asset-tag">${escapeHtml(a.asset_tag || '\u2014')}</div>
            <div class="browse-asset-sub">${escapeHtml(model)}${serial ? ' \u00b7 SN: ' + escapeHtml(serial) : ''}</div>
          </div>
          <span class="status-tag ${sClass}">${escapeHtml(sName)}</span>
          <span class="browse-asset-chevron">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
          </span>
        </div>`;
    }).join('');

    if (append) {
      assetList.insertAdjacentHTML('beforeend', html);
    } else {
      if (rows.length === 0) {
        assetList.innerHTML = '<div style="text-align:center; padding:32px; color:var(--color-text-muted);">No assets found</div>';
      } else {
        assetList.innerHTML = html;
      }
    }
  }

  // ── Asset tap → detail sheet ──
  assetList.addEventListener('click', (e) => {
    const row = e.target.closest('.browse-asset-row');
    if (!row) return;
    const id = parseInt(row.dataset.id, 10);
    const asset = assets.find(a => a.id === id);
    if (asset) showAssetSheet(asset);
  });

  // ── Load more + infinite scroll ──
  lmBtn.addEventListener('click', () => {
    offset += PAGE_SIZE;
    fetchAssets(true);
    // After first "Keep Loading" tap, activate infinite scroll
    if (!infiniteScrollActive) {
      infiniteScrollActive = true;
      observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && !loading && assets.length < total) {
          offset += PAGE_SIZE;
          fetchAssets(true);
        }
      }, { rootMargin: '200px' });
      observer.observe(sentinel);
    }
  });

  // ── Filter sheet ──
  let sheetBackdrop = null;
  let sheetEl = null;

  function openFilterSheet() {
    // Build pending filters (copy of current)
    const pending = { ...filters };
    const pendingLabels = { ...labels };

    sheetBackdrop = document.createElement('div');
    sheetBackdrop.className = 'bottom-sheet-backdrop';
    sheetEl = document.createElement('div');
    sheetEl.className = 'bottom-sheet';
    sheetEl.style.maxHeight = '80dvh';
    document.body.appendChild(sheetBackdrop);
    document.body.appendChild(sheetEl);

    function close() {
      sheetBackdrop.classList.remove('open');
      sheetEl.classList.remove('open');
      setTimeout(() => { sheetBackdrop.remove(); sheetEl.remove(); sheetBackdrop = null; sheetEl = null; }, 300);
    }

    sheetEl.innerHTML = `
      <div class="sheet-handle"></div>
      <div class="sheet-header">
        <h2>Filter Assets</h2>
        <button class="btn btn-secondary btn-sm" id="filter-clear">Clear all</button>
      </div>
      <div class="sheet-body" style="padding: 0 16px 16px;">
        <div id="filter-dims"></div>
      </div>
      <div class="sheet-footer">
        <button class="btn btn-secondary" style="flex:1" id="filter-cancel">Cancel</button>
        <button class="btn btn-primary" style="flex:1" id="filter-apply">Apply Filters</button>
      </div>
    `;

    const dimsContainer = sheetEl.querySelector('#filter-dims');

    // Render each dimension
    DIMENSIONS.forEach(dim => {
      const section = document.createElement('div');
      section.className = 'filter-section';
      section.innerHTML = `
        <div class="filter-section-title">${escapeHtml(dim.label)}</div>
        <div class="filter-options" data-dim="${dim.key}">
          <span class="filter-options-loading"><span class="spinner" style="width:16px;height:16px;border-width:2px;"></span></span>
        </div>
      `;
      dimsContainer.appendChild(section);

      // Load values
      loadDimension(dim, section.querySelector('.filter-options'), pending);
    });

    // Clear all
    sheetEl.querySelector('#filter-clear').addEventListener('click', () => {
      Object.keys(pending).forEach(k => delete pending[k]);
      Object.keys(pendingLabels).forEach(k => delete pendingLabels[k]);
      sheetEl.querySelectorAll('.filter-opt.selected').forEach(o => o.classList.remove('selected'));
    });

    // Cancel
    sheetEl.querySelector('#filter-cancel').addEventListener('click', close);
    sheetBackdrop.addEventListener('click', close);

    // Apply
    sheetEl.querySelector('#filter-apply').addEventListener('click', () => {
      // Copy pending into real filters
      Object.keys(filters).forEach(k => delete filters[k]);
      Object.keys(labels).forEach(k => delete labels[k]);
      Object.assign(filters, pending);
      Object.assign(labels, pendingLabels);
      close();
      renderChips();
      resetAndFetch();
    });

    // Delegate clicks on filter options
    dimsContainer.addEventListener('click', (e) => {
      const opt = e.target.closest('.filter-opt');
      if (!opt) return;
      const dimKey = opt.closest('.filter-options').dataset.dim;
      const id = opt.dataset.id;
      const lbl = opt.dataset.label;

      if (opt.classList.contains('selected')) {
        opt.classList.remove('selected');
        delete pending[dimKey];
        delete pendingLabels[dimKey];
      } else {
        // Single-select per dimension
        opt.closest('.filter-options').querySelectorAll('.filter-opt.selected').forEach(o => o.classList.remove('selected'));
        opt.classList.add('selected');
        pending[dimKey] = id;
        pendingLabels[dimKey] = lbl;
      }
    });

    requestAnimationFrame(() => {
      sheetBackdrop.classList.add('open');
      sheetEl.classList.add('open');
    });
  }

  async function loadDimension(dim, container, pending) {
    if (dimCache.has(dim.key)) {
      renderDimOptions(dim.key, dimCache.get(dim.key), container, pending);
      return;
    }
    try {
      const res = await dim.loader();
      const rows = res.rows || [];
      dimCache.set(dim.key, rows);
      renderDimOptions(dim.key, rows, container, pending);
    } catch {
      container.innerHTML = '<span style="font-size:0.8rem; color:var(--color-text-muted);">Failed to load</span>';
    }
  }

  function renderDimOptions(dimKey, rows, container, pending) {
    if (rows.length === 0) {
      container.innerHTML = '<span style="font-size:0.8rem; color:var(--color-text-muted);">None available</span>';
      return;
    }
    container.innerHTML = rows.map(r => {
      const selected = String(pending[dimKey]) === String(r.id) ? ' selected' : '';
      return `<button class="filter-opt${selected}" data-id="${r.id}" data-label="${escapeHtml(r.name || '')}">${escapeHtml(r.name || '\u2014')}</button>`;
    }).join('');
  }

  // ── Asset detail sheet ──
  function showAssetSheet(asset) {
    const backdrop = document.createElement('div');
    backdrop.className = 'bottom-sheet-backdrop';
    const sheet = document.createElement('div');
    sheet.className = 'bottom-sheet';
    sheet.style.maxHeight = '85dvh';
    document.body.appendChild(backdrop);
    document.body.appendChild(sheet);

    function close() {
      backdrop.classList.remove('open');
      sheet.classList.remove('open');
      setTimeout(() => { backdrop.remove(); sheet.remove(); }, 300);
    }

    const a = asset;
    const sName = a.status_label?.name || '\u2014';
    const sType = a.status_label?.status_type || '';
    const sClass = statusClass(sType);
    const wlan = a.custom_fields?.[MAC_COLUMN_WLAN]?.value || '';
    const lan  = a.custom_fields?.[MAC_COLUMN_LAN]?.value || '';
    const ip   = a.custom_fields?.[MAC_COLUMN_IP]?.value || '';

    sheet.innerHTML = `
      <div class="sheet-handle"></div>
      <div class="sheet-header">
        <h2>Asset Details</h2>
        <button class="btn btn-secondary btn-sm" id="detail-close-top">Close</button>
      </div>
      <div class="sheet-body" style="padding:16px;">
        <h3 style="margin:0 0 4px; font-size:1.05rem;">${escapeHtml(a.name || 'Unnamed')}</h3>
        <div style="font-size:0.85rem; color:var(--color-text-muted); margin-bottom:14px;">
          Tag: <span class="mono">${escapeHtml(a.asset_tag || '\u2014')}</span>
          &nbsp;|&nbsp; Serial: <span class="mono">${escapeHtml(a.serial || '\u2014')}</span>
        </div>
        <dl class="asset-detail-dl">
          <dt>Model</dt><dd>${escapeHtml(a.model?.name || '\u2014')}</dd>
          <dt>Category</dt><dd>${escapeHtml(a.category?.name || '\u2014')}</dd>
          <dt>Status</dt><dd><span class="status-tag ${sClass}">${escapeHtml(sName)}</span></dd>
          <dt>Assigned To</dt><dd>${a.assigned_to ? escapeHtml(a.assigned_to.name || '\u2014') : '<span style="color:var(--color-text-muted)">Not assigned</span>'}</dd>
          <dt>Location</dt><dd>${escapeHtml(a.location?.name || a.rtd_location?.name || '\u2014')}</dd>
          ${wlan ? `<dt>WLAN MAC</dt><dd class="mono">${escapeHtml(wlan)}</dd>` : ''}
          ${lan  ? `<dt>LAN MAC</dt><dd class="mono">${escapeHtml(lan)}</dd>` : ''}
          ${ip   ? `<dt>IP</dt><dd class="mono">${escapeHtml(ip)}</dd>` : ''}
          ${a.notes ? `<dt>Notes</dt><dd>${escapeHtml(a.notes)}</dd>` : ''}
        </dl>
      </div>
      <div class="sheet-footer">
        <button class="btn btn-secondary" style="flex:1" id="detail-close">Close</button>
        <button class="btn btn-primary" style="flex:1" id="detail-edit">Edit Asset</button>
      </div>
    `;

    sheet.querySelector('#detail-close-top').addEventListener('click', close);
    sheet.querySelector('#detail-close').addEventListener('click', close);
    sheet.querySelector('#detail-edit').addEventListener('click', () => {
      close();
      router.navigate('/edit-asset');
    });
    backdrop.addEventListener('click', close);

    requestAnimationFrame(() => {
      backdrop.classList.add('open');
      sheet.classList.add('open');
    });
  }

  // ── Init ──
  renderSortRow();
  renderChips();
  fetchAssets(false);

  // ── Cleanup ──
  return () => {
    clearTimeout(searchTimer);
    if (observer) { observer.disconnect(); observer = null; }
    if (sheetBackdrop) { sheetBackdrop.remove(); }
    if (sheetEl) { sheetEl.remove(); }
  };
}
